import { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "vault-display-prefs";

const DEFAULTS = {
  fontSize: 100,      // percentage: 75-125 in 5% steps
  zoom: 100,           // percentage: 75-125 in 5% steps
  theme: "light",      // light | dark | high-contrast
  compact: false,      // compact view mode
  collapsedSections: {},
};

const DisplayContext = createContext(null);

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
}

function savePrefs(prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
}

export function DisplayProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => { savePrefs(prefs); }, [prefs]);

  // Apply to document
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = `${prefs.fontSize}%`;
    root.setAttribute("data-theme", prefs.theme);
    root.setAttribute("data-compact", prefs.compact ? "true" : "false");

    // Zoom via CSS transform on main content
    const appLayout = document.querySelector("[data-testid='app-layout']");
    if (appLayout && prefs.zoom !== 100) {
      appLayout.style.transform = `scale(${prefs.zoom / 100})`;
      appLayout.style.transformOrigin = "top left";
      appLayout.style.width = `${10000 / prefs.zoom}%`;
      appLayout.style.height = `${10000 / prefs.zoom}%`;
    } else if (appLayout) {
      appLayout.style.transform = "";
      appLayout.style.transformOrigin = "";
      appLayout.style.width = "";
      appLayout.style.height = "";
    }
  }, [prefs]);

  const setFontSize = useCallback((size) => setPrefs(p => ({ ...p, fontSize: size })), []);
  const setZoom = useCallback((z) => setPrefs(p => ({ ...p, zoom: z })), []);
  const setTheme = useCallback((t) => setPrefs(p => ({ ...p, theme: t })), []);
  const setCompact = useCallback((c) => setPrefs(p => ({ ...p, compact: c })), []);
  const toggleSection = useCallback((id) => setPrefs(p => ({
    ...p, collapsedSections: { ...p.collapsedSections, [id]: !p.collapsedSections[id] }
  })), []);
  const isSectionCollapsed = useCallback((id) => !!prefs.collapsedSections[id], [prefs.collapsedSections]);
  const collapseAll = useCallback((ids) => setPrefs(p => {
    const next = { ...p.collapsedSections };
    ids.forEach(id => { next[id] = true; });
    return { ...p, collapsedSections: next };
  }), []);
  const expandAll = useCallback((ids) => setPrefs(p => {
    const next = { ...p.collapsedSections };
    ids.forEach(id => { next[id] = false; });
    return { ...p, collapsedSections: next };
  }), []);
  const resetAll = useCallback(() => setPrefs({ ...DEFAULTS }), []);

  return (
    <DisplayContext.Provider value={{
      ...prefs, setFontSize, setZoom, setTheme, setCompact,
      toggleSection, isSectionCollapsed, collapseAll, expandAll, resetAll,
    }}>
      {children}
    </DisplayContext.Provider>
  );
}

export function useDisplay() {
  return useContext(DisplayContext);
}
